import { TFunction } from 'i18next';

// --- Add near other helpers/states ---
export const VIDEO_EXT_WHITELIST = [
  'mp4',
  'm4v',
  'mov',
  'webm',
  'mkv',
  'avi',
  'ogv',
  'ogg',
  'm4p',
  'm4b',
];
export const MAX_VIDEO_BYTES = 5 * 1024 * 1024 * 1024; //5 Gb
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10MB
export const IMG_EXT_WHITELIST = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'svg'];
export function hasVideoMime(f: File) {
  return typeof f.type === 'string' && f.type.toLowerCase().startsWith('video/');
}

export function hasAllowedExt(f: File) {
  const name = f.name || '';
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  return VIDEO_EXT_WHITELIST.includes(ext);
}

// Very quick container sniff: read first ~64 bytes once, check well-known signatures
export async function sniffVideoSignature(f: File): Promise<boolean> {
  // Some browsers may block slice on empty files
  if (f.size === 0) return false;
  const head = await f.slice(0, 64).arrayBuffer();
  const bytes = new Uint8Array(head);

  // Helper to read ASCII
  const toAscii = (start: number, len: number) =>
    Array.from(bytes.slice(start, start + len))
      .map((c) => String.fromCharCode(c))
      .join('');

  // MP4/MOV check: "ftyp" box usually at byte 4
  // 0..3 size, 4..7 "ftyp"
  if (bytes.length >= 12) {
    const brand = toAscii(4, 4);
    if (brand === 'ftyp') return true;
  }

  // WebM/Matroska EBML header: 0x1A,0x45,0xDF,0xA3 (→ “\x1A\x45\xDF\xA3”)
  if (bytes.length >= 4) {
    if (bytes[0] === 0x1a && bytes[1] === 0x45 && bytes[2] === 0xdf && bytes[3] === 0xa3) {
      return true; // MKV/WebM family
    }
  }

  // Ogg: "OggS" at start
  if (bytes.length >= 4 && toAscii(0, 4) === 'OggS') return true;

  // AVI: "RIFF" at 0 and "AVI " at 8
  if (bytes.length >= 12 && toAscii(0, 4) === 'RIFF' && toAscii(8, 4) === 'AVI ') return true;

  // If unsure, return false to be strict (or true to be lenient)
  return false;
}

export async function validateVideoFile(f: File, t: TFunction): Promise<string | null> {
  if (!f) return t('courses.lessons.lessonForm.noFile');
  if (f.size > MAX_VIDEO_BYTES) return t('courses.lessons.lessonForm.tooLarge');
  // 1) MIME check
  if (hasVideoMime(f)) return null;
  // 2) Extension check
  if (hasAllowedExt(f)) return null;
  // 3) Signature sniff (slowest, but robust)
  try {
    const looksLikeVideo = await sniffVideoSignature(f);
    if (looksLikeVideo) return null;
  } catch {
    // ignore and fall through to error
  }
  return t('courses.lessons.lessonForm.fileNotRecognized');
}

export function isImageFile(f: File) {
  // Prefer MIME, fallback to extension when type is empty
  if (f.type && f.type.toLowerCase().startsWith('image/')) return true;
  const ext = (f.name.split('.').pop() || '').toLowerCase();
  return IMG_EXT_WHITELIST.includes(ext);
}

export function validateImages(files: File[], t: TFunction) {
  const valid: File[] = [];
  const errors: string[] = [];

  for (const f of files) {
    if (!isImageFile(f)) {
      errors.push(t('leads.addActionForm.errNotImage', { filename: f.name }));

      continue;
    }
    if (f.size > MAX_IMAGE_BYTES) {
      const mb = Math.ceil(f.size / (1024 * 1024));
      errors.push(
        t('leads.addActionForm.errNotImage', {
          filename: f.name,
          size: mb,
          maxSize: MAX_IMAGE_BYTES,
        }),
      );

      continue;
    }
    valid.push(f);
  }
  return { valid, errors };
}
